export type PositionShiftResult = {
  id: string;
  oldTop: number;
  newTop: number;
  height: number;
};

export type PositionShiftInput = {
  id: string;
  top: number;
  height: number;
};

export function runPositionShift(
  elements: PositionShiftInput[],
  spacing: number,
  topPadding: number = 0,
): PositionShiftResult[] {
  const PADDING = spacing;

  // Sort elements by their original top position
  const sortedElements = [...elements].sort((a, b) => a.top - b.top);

  // Group overlapping elements into bundles
  const bundles: PositionShiftInput[][] = [];
  let currentBundle: PositionShiftInput[] = [];

  sortedElements.forEach((element, index) => {
    if (index === 0) {
      currentBundle.push(element);
      return;
    }

    const lastElement = currentBundle[currentBundle.length - 1];
    const overlap = lastElement.top + lastElement.height + PADDING > element.top;

    if (overlap) {
      currentBundle.push(element);
    } else {
      bundles.push(currentBundle);
      currentBundle = [element];
    }
  });

  if (currentBundle.length > 0) {
    bundles.push(currentBundle);
  }

  // Process each bundle to position elements
  // Start at topPadding so a stack pushed to the top keeps a gap from the chart edge.
  let lastUsedPosition = topPadding; // Track the last used position
  const results: PositionShiftResult[] = [];

  bundles.forEach((bundle) => {
    // Calculate total height of bundle including padding
    const bundleHeight = bundle.reduce((sum, el, i) => {
      return sum + el.height + (i < bundle.length - 1 ? PADDING : 0);
    }, 0);

    // Calculate vertical center of original positions in bundle
    const bundleStart = bundle[0].top;
    const bundleEnd = bundle[bundle.length - 1].top + bundle[bundle.length - 1].height;
    const originalCenter = bundleStart + (bundleEnd - bundleStart) / 2;

    // Center the bundle around the original center point
    const newBundleStart = originalCenter - bundleHeight / 2;
    let currentTop = Math.max(lastUsedPosition, newBundleStart); // Use lastUsedPosition as minimum

    // Position elements in bundle
    bundle.forEach((element) => {
      results.push({
        id: element.id,
        oldTop: element.top,
        newTop: currentTop,
        height: element.height,
      });
      currentTop += element.height + PADDING;
    });

    // Update the last used position for the next bundle
    lastUsedPosition = currentTop;
  });

  return results;
}
